import { createReducer, INITIAL_STATE } from '@shared/redux';
import { SelectOption } from '@shared/select/select.types';
import {
    fieldOptionsToSelectOptions,
    enumToSelectOptions,
} from './interface.utils';
import { FIELD_TYPE, STATUS_OPTIONS } from '../campaigns-filter.types';
import { AVAILABLE_FIELDS, OPERATORS_BY_FIELD_TYPE } from '../campaigns-filter.constants';
import { isNewFilter } from '../filters/filters.utils';

export const OPEN_SAVE_MODAL = '@campaigns/campaigns-filters/interface/OPEN_SAVE_MODAL';
export const CLOSE_SAVE_MODAL = '@campaigns/campaigns-filters/interface/CLOSE_SAVE_MODAL';

export const ON_CHANGE_CUSTOM_FILTERS = '@campaigns/campaigns-filters/interface/ON_CHANGE_CUSTOM_FILTERS';

export const ON_CHANGE_FIELD = '@campaigns/campaigns-filters/interface/ON_CHANGE_FIELD';
export const ON_CHANGE_OPERATOR = '@campaigns/campaigns-filters/interface/ON_CHANGE_OPERATOR';
export const ON_CHANGE_VALUE = '@campaigns/campaigns-filters/interface/ON_CHANGE_VALUE';

export const ON_CLICK_APPLY = '@campaigns/campaigns-filters/interface/ON_CLICK_APPLY';
export const ON_CLICK_CLEAR = '@campaigns/campaigns-filters/interface/ON_CLICK_CLEAR';
export const ON_CLICK_REMOVE_RULE = '@campaigns/campaigns-filters/interface/ON_CLICK_REMOVE_RULE';

export const DISABLE_APPLY_RULE = '@campaigns/campaigns-filters/interface/DISABLE_APPLY_RULE';
export const ENABLE_APPLY_RULE = '@campaigns/campaigns-filters/interface/ENABLE_APPLY_RULE';

export const DISABLE_OPEN_SAVE_MODAL = '@campaigns/campaigns-filters/interface/DISABLE_OPEN_SAVE_MODAL';
export const ENABLE_OPEN_SAVE_MODAL = '@campaigns/campaigns-filters/interface/ENABLE_OPEN_SAVE_MODAL';

export const DISABLE_CLEAR_RULES = '@campaigns/campaigns-filters/interface/DISABLE_CLEAR_RULES';
export const ENABLE_CLEAR_RULES = '@campaigns/campaigns-filters/interface/ENABLE_CLEAR_RULES';

export const SAVE_FILTER = '@campaigns/campaigns-filters/interface/SAVE_FILTER';
export const CANCEL_SAVE = '@campaigns/campaigns-filters/interface/CANCEL_SAVE';

export const CHANGE_OPTIONS = '@campaigns/campaigns-filters/interface/CHANGE_OPTIONS';

export const getCampaignsFiltersInterface = state => state.campaigns.campaignsFilters.interface;

export const openSaveModal = () => ({
    type: OPEN_SAVE_MODAL,
    payload: {},
});

export const closeSaveModal = () => ({
    type: CLOSE_SAVE_MODAL,
    payload: {},
});

export const onChangeField = newField => ({
    type: ON_CHANGE_FIELD,
    payload: newField,
});

export const onChangeOperator = newOperator => ({
    type: ON_CHANGE_OPERATOR,
    payload: newOperator,
});

export const onChangeValue = newValue => ({
    type: ON_CHANGE_VALUE,
    payload: newValue,
});

export const onClickApply = rule => ({
    type: ON_CLICK_APPLY,
    payload: rule,
});

export const onClickClear = () => ({
    type: ON_CLICK_CLEAR,
    payload: {},
});

export const onClickRemoveRule = rule => ({
    type: ON_CLICK_REMOVE_RULE,
    payload: rule,
});

export const saveFilter = ({ filterName }) => ({
    type: SAVE_FILTER,
    payload: { filterName },
});

export const disableApplyRule = () => ({
    type: DISABLE_APPLY_RULE,
    payload: {},
});
export const enableApplyRule = () => ({
    type: ENABLE_APPLY_RULE,
    payload: {},
});

export const disableOpenSaveModal = () => ({
    type: DISABLE_OPEN_SAVE_MODAL,
    payload: {},
});

export const enableOpenSaveModal = () => ({
    type: ENABLE_OPEN_SAVE_MODAL,
    payload: {},
});

export const disableClearRules = () => ({
    type: DISABLE_CLEAR_RULES,
    payload: {},
});

export const enableClearRules = () => ({
    type: ENABLE_CLEAR_RULES,
    payload: {},
});

export const onChangeCustomFilters = selectedFilter => ({
    type: ON_CHANGE_CUSTOM_FILTERS,
    payload: { selectedFilter },
});

export const changeOptions = ({ operatorOptions }) => ({
    type: CHANGE_OPTIONS,
    payload: {
        operatorOptions,
    },
});

export type CampaignsFiltersInterface = {
    isSaveModalOpen: boolean,
    isApplyRuleDisabled: boolean,
    isClearRulesDisabled: boolean,
    isSaveFilterDisabled: boolean,
    isOperatorDisabled: boolean,
    isValueDisabled: boolean,
    field: SelectOption,
    operator: SelectOption,
    value: SelectOption,
    fieldOptions: Array<SelectOption>;
    operatorOptions: Array<SelectOption>;
    valueOptions?: Array<SelectOption>;
}

const initialState = {
    isSaveModalOpen: false,
    isApplyRuleDisabled: true,
    isClearRulesDisabled: true,
    isSaveFilterDisabled: true,
    isOperatorDisabled: true,
    isValueDisabled: true,
    field: null,
    operator: null,
    value: null,
    fieldOptions: fieldOptionsToSelectOptions(AVAILABLE_FIELDS),
    operatorOptions: enumToSelectOptions(OPERATORS_BY_FIELD_TYPE[FIELD_TYPE.ENUM]),
    valueOptions: enumToSelectOptions(STATUS_OPTIONS),
};

export const interfaceReducers = createReducer({
    [INITIAL_STATE]: initialState,
    [OPEN_SAVE_MODAL]: state => ({
        ...state,
        isSaveModalOpen: true,
    }),
    [CLOSE_SAVE_MODAL]: state => ({
        ...state,
        isSaveModalOpen: false,
    }),
    [ON_CHANGE_FIELD]: (state, action) => ({
        ...state,
        field: action.payload,
        isOperatorDisabled: false,
        isValueDisabled: true,
        operator: null,
        value: null,
    }),
    [ON_CHANGE_OPERATOR]: (state, action) => ({
        ...state,
        operator: action.payload,
        isValueDisabled: false,
        value: null,
    }),
    [ON_CHANGE_VALUE]: (state, action) => ({
        ...state,
        value: action.payload,
        isApplyRuleDisabled: false,
    }),
    [ON_CLICK_APPLY]: state => ({
        ...state,
        field: null,
        operator: null,
        value: null,
        isOperatorDisabled: true,
        isValueDisabled: true,
        isClearRulesDisabled: false,
        isApplyRuleDisabled: true,
        isSaveFilterDisabled: false,
    }),
    [ON_CLICK_CLEAR]: () => initialState,
    [SAVE_FILTER]: state => ({
        ...initialState,
        isSaveModalOpen: state.isSaveModalOpen,
    }),
    [CANCEL_SAVE]: state => ({
        ...state,
        isSaveModalOpen: false,
    }),
    [DISABLE_APPLY_RULE]: state => ({
        ...state,
        isApplyRuleDisabled: true,
    }),
    [ENABLE_APPLY_RULE]: state => ({
        ...state,
        isApplyRuleDisabled: false,
    }),
    [DISABLE_OPEN_SAVE_MODAL]: state => ({
        ...state,
        isSaveFilterDisabled: true,
    }),
    [ENABLE_OPEN_SAVE_MODAL]: state => ({
        ...state,
        isSaveFilterDisabled: false,
    }),
    [DISABLE_CLEAR_RULES]: state => ({
        ...state,
        isClearRulesDisabled: true,
    }),
    [ENABLE_CLEAR_RULES]: state => ({
        ...state,
        isClearRulesDisabled: false,
    }),
    [ON_CHANGE_CUSTOM_FILTERS]: (state, action) => {
        const { selectedFilter } = action.payload;
        return {
            ...state,
            field: null,
            operator: null,
            value: null,
            isOperatorDisabled: true,
            isValueDisabled: true,
            isApplyRuleDisabled: true,
            isClearRulesDisabled: isNewFilter(selectedFilter),
            isSaveFilterDisabled: true,
        };
    },
    [CHANGE_OPTIONS]: (state, action) => ({
        ...state,
        ...action.payload,
    }),
});
